import { Component, Input, Output, ViewEncapsulation, EventEmitter } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { DisplayValue } from '../../models/browser.model';
import { ResortDetail } from '../../models/presentation.model';
import { ResortApplicationBase } from '../../services/resort-application.service';
import { LabelNames, Member, OvsComponent } from '../../shared/core/ovs.core';

import * as moment from 'moment';
import { UserActivityLogger, UserActivityLoggerOptions } from '../../instrumentation/user-activity-logger.service';
import { CheckoutPageEvents } from '../../instrumentation/resort-custom-events';

@Component({
  selector: 'checkout-guests',
  templateUrl: './checkout-guests.component.html',
  styleUrls: ['./checkout-guests.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CheckoutGuestsComponent extends OvsComponent {
  @Input() checkoutForm: FormGroup;
  @Input() members: Member[] = [];
  @Input() resort: ResortDetail;
  @Input() application: ResortApplicationBase;
  @Input() usedMembers: number[] = [];

  @Output() guestIsPurchaserChecked: EventEmitter<any> = new EventEmitter();
  @Output() guestMemberChanged: EventEmitter<any> = new EventEmitter();

  months: DisplayValue<number>[];
  years: DisplayValue<number>[];
  guests: FormArray;
  firstGuestForm: FormGroup;
  termsConditionsUrl: string;
  privacyPolicyUrl: string;
  marketingMsg: string;
  public hideSelectGuest: boolean = false;

  constructor() {
    super();
  }

  initialize() {
    let months: DisplayValue<number>[] = [];
    let years: DisplayValue<number>[] = [];
    this.guests = this.checkoutForm.get('guests') as FormArray;

    for (let index: number = 0; index < 12; index++) {
      months.add(new DisplayValue(index + 1, moment(new Date(1900, index)).format("MM")));
    }

    for (let index: number = new Date().getFullYear(); index > new Date().getFullYear() - 120; index--) {
      years.add(new DisplayValue(index, index.toString()));
    }

    this.months = months;
    this.years = years;

    this.addFirstGuest();

    this.guests.valueChanges.subscribe(() => {
      setTimeout(() => {
        this.checkForDuplicates();
      }, 0);
    });

    if(this.configuration.Labels.length > 0) {
      const defaultMarketingMsg = `I consent to receive autodialed and/or pre-recorded telemarketing calls and text messages at the telephone numbers provided concerning products and services offered by or on behalf of ${this.configuration.Partner.LongName}. I understand that consent is not required to make any purchase of goods or services from ${this.configuration.Partner.LongName} and my consent may be revoked at any time. Message and data rates may apply.`;
      this.marketingMsg = this.configuration.Labels ? this.configuration.Labels.first(l => l.Name == LabelNames.MarketingOptInLabel).DisplayText : defaultMarketingMsg;
      this.marketingMsg = this.marketingMsg.replace(new RegExp('{{ configuration.Partner.LongName }}', 'g'), this.configuration.Partner.LongName);
    }

    const footerLinks = this.configuration.FooterLinks;
    const legalLinks = [];

    footerLinks.forEach((link) => {
        if (
            (link.DisplayText.toLowerCase() === "privacy policy") ||
            (link.DisplayText.toLowerCase() === "terms & conditions") ||
            (link.DisplayText.toLowerCase() === "terms and conditions")
        ) {
            legalLinks.push(link);
        }
    })

    if (legalLinks.length) {
        this.termsConditionsUrl = legalLinks[1] && legalLinks[1].NavigationUrl;
        this.privacyPolicyUrl = legalLinks[0] && legalLinks[0].NavigationUrl;
    }
  }

  private updateUsedmembers(item, index): void {
    if(this.usedMembers[index]) {
      this.usedMembers[index] = item.Id;
    } else {
      this.usedMembers.push(item.Id);
    }
  }

  /**
   * @param index  number
   */
  removeUsedMembers(index) {
    if (this.usedMembers[index]) {
      this.usedMembers.splice(index, 1); // 
    }
  }

  /**
   * @param index  number
   * @param item member
   */
  public checkMemberSelected(item, index): void {
    this.updateUsedmembers(item, index)
  }

  /**
   * Check if there are guest duplicated
   */
  private checkForDuplicates(): void {
    let guestDuplicatesCount: any = {};

    this.guests.controls.forEach((form: FormGroup, index: number) => {
      const FirstName = form.get('FirstName');
      const LastName = form.get('LastName');
      const DobMonth = form.get('DobMonth');
      const DobDay = form.get('DobDay');
      const DobYear = form.get('DobYear');
      
      form.get('isDuplicated').setValue(false, {emitEvent: false});

      if (FirstName.valid && LastName.valid && DobMonth.valid && DobDay.valid && DobYear.valid) {
        const key = `${FirstName.value}-${LastName.value}-${DobMonth.value}-${DobDay.value}-${DobYear.value}`;
        guestDuplicatesCount[key] = (guestDuplicatesCount[key] || 0) + 1;
  
        if (guestDuplicatesCount[key] > 1 ) {
          form.get('isDuplicated').setValue(undefined, {emitEvent: false});
        }
      }
    });
  }

  /**
   * Add First/Main guest
   */
  private addFirstGuest(): void {
    let firstName: string;
    let lastName: string;
    let selectedGuest: Member;
    let email: string;

    if (this.application.configuration.Member) {
      firstName = this.application.currentBooking.FirstName;
      lastName = this.application.currentBooking.LastName;
      selectedGuest = this.members.where(g => g.LastName == lastName && g.FirstName == firstName).firstOrDefault();
      email = this.application.currentBooking.EmailAddress;
    } else {
      firstName = '';
      lastName = '';
      selectedGuest = null;
      email = '';
    }

    this.firstGuestForm = new FormGroup({
      selectedGuest: new FormControl(selectedGuest),
      FirstName: new FormControl(firstName, [Validators.required, Validators.pattern(/^(?!\s*$)[a-zA-Z ]+$/)]),
      LastName: new FormControl(lastName, [Validators.required, Validators.pattern(/^(?!\s*$)[a-zA-Z ]+$/)]),
      EmailAddress: new FormControl(email, [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)]),
      DateOfBirth: new FormControl(''),
      DobMonth: new FormControl('', Validators.required),
      DobDay: new FormControl('', Validators.required),
      DobYear: new FormControl('', Validators.required),
      PhoneNumber: new FormControl('', Validators.required),
      CellPhone: new FormControl(''),
      ordinal: new FormControl(this.getOrdianlText(1)),
      guestIsPurchaserChecked: new FormControl(true),
      IsTextMessagingApproved: new FormControl(true),
      isDuplicated: new FormControl(false, Validators.required)
    });

    this.guests.push(this.firstGuestForm);

    this.updateUsedmembers(this.members.find(g => g.IsPrimaryMember === true), 0);

    this.firstGuestForm.get('guestIsPurchaserChecked').valueChanges.subscribe(value => {
      this.guestIsPurchaserChecked.emit(value);
    });

    this.firstGuestForm.get('FirstName').valueChanges.subscribe(value => {
      this.onFirstGuestMemberChanged();
    });

    this.firstGuestForm.get('LastName').valueChanges.subscribe(value => {
      this.onFirstGuestMemberChanged();
    });

    this.firstGuestForm.get('selectedGuest').valueChanges.subscribe(value => {
      UserActivityLogger.logEvent(CheckoutPageEvents.FirstGuestNameUpdated, {}, UserActivityLoggerOptions.AppInsightsOnly);
      this.onFirstGuestMemberChanged();
    });
  }

  /**
   * Add new guest
   */
  addGuest(): void {
    const newPosition = this.guests.length + 1;
    this.guests.push(
      new FormGroup({
        selectedGuest: new FormControl(''),
        FirstName: new FormControl('', [Validators.required, Validators.pattern(/^(?!\s*$)[a-zA-Z ]+$/)]),
        LastName: new FormControl('', [Validators.required, Validators.pattern(/^(?!\s*$)[a-zA-Z ]+$/)]),
        EmailAddress: new FormControl(''),
        DateOfBirth: new FormControl(''),
        DobMonth: new FormControl('', Validators.required),
        DobDay: new FormControl('', Validators.required),
        DobYear: new FormControl('', Validators.required),
        PhoneNumber: new FormControl(''),
        CellPhone: new FormControl(''),
        ordinal: new FormControl(this.getOrdianlText(newPosition)),
        guestIsPurchaserChecked: new FormControl(false),
        IsTextMessagingApproved: new FormControl(true),
        isDuplicated: new FormControl(false, Validators.required)
      })
    );
  }

  /**
   * Is called when Main Guest FirstName/LastName changed
   */
  private onFirstGuestMemberChanged(): void {
    this.removeUsedMembers(0);
    setTimeout(() => {
      this.guestMemberChanged.emit(this.firstGuestForm.value);
    }, 0);
  }

  /**
   * Remove guest at index
   * @param index 
   */
  removeGuest(index: number): void {
    this.removeUsedMembers(index);
    this.guests.removeAt(index);

    /**
     * Reset cardinals numbers
     */
    this.guests.controls.forEach((control, index) => {
      const ordinalText = this.getOrdianlText(index + 1);

      this.guests.at(index).get('ordinal').setValue(ordinalText);
    });
  }

  /**
   * @param num Position number
   * @returns ordinal text
   */
  private getOrdianlText(num: number): string {
    const ordinalMap = {
      1: 'first',
      2: 'second',
      3: 'third',
      4: 'fourth',
      5: 'fifth',
      6: 'sixth',
      7: 'seventh',
      8: 'eighth',
      9: 'ninth',
      10: 'tenth'
    };

    return ordinalMap[num] || '';
  }

}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/check-out/checkout-guests/checkout-guests.component.ts